import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Check,
  Sparkles,
  Zap,
  Crown,
  Building2,
  Loader2,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { SubscriptionPlan, UserSubscription } from "@shared/schema";

const planIcons = {
  free: Sparkles,
  basic: Zap,
  pro: Crown,
  enterprise: Building2,
};

const planColors = {
  free: "text-muted-foreground",
  basic: "text-chart-2",
  pro: "text-chart-4",
  enterprise: "text-primary",
};

interface PlanFeatures {
  [key: string]: string[];
}

const defaultFeatures: PlanFeatures = {
  free: [
    "Basic market data (15-min delay)",
    "1 watchlist (up to 10 symbols)",
    "Basic portfolio tracking",
    "Email support",
  ],
  basic: [
    "Real-time market data",
    "5 watchlists (up to 50 symbols each)",
    "Advanced portfolio analytics",
    "Priority email support",
    "Trade history export",
  ],
  pro: [
    "Real-time streaming data",
    "Unlimited watchlists",
    "Advanced charts & indicators",
    "API access",
    "Phone support",
    "Multiple accounts",
  ],
  enterprise: [
    "Everything in Pro",
    "Custom integrations",
    "Dedicated account manager",
    "White-label options",
    "Custom reporting",
    "SLA guarantee",
  ],
};

function PlanCard({
  plan,
  isCurrentPlan,
  onSelect,
  loading,
}: {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  onSelect: () => void;
  loading: boolean;
}) {
  const Icon = planIcons[plan.tier as keyof typeof planIcons] || Sparkles;
  const colorClass = planColors[plan.tier as keyof typeof planColors] || "text-primary";
  const features = (plan.features as string[]) || defaultFeatures[plan.tier] || [];
  const monthlyPrice = parseFloat(plan.monthlyPrice?.toString() || "0");

  return (
    <Card className={`relative ${isCurrentPlan ? "border-primary" : ""}`}>
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary">Current Plan</Badge>
        </div>
      )}
      <CardHeader className="text-center pt-8">
        <div className={`h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4 ${colorClass}`}>
          <Icon className="h-6 w-6" />
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <div className="mt-4">
          {monthlyPrice === 0 ? (
            <div className="text-3xl font-bold">Free</div>
          ) : (
            <div>
              <span className="text-3xl font-bold">${monthlyPrice}</span>
              <span className="text-muted-foreground">/month</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Separator />
        <ul className="space-y-3">
          {features.map((feature: string, index: number) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-chart-1 shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <Button
          className="w-full"
          variant={isCurrentPlan ? "secondary" : "default"}
          disabled={isCurrentPlan || loading}
          onClick={onSelect}
          data-testid={`button-select-${plan.tier}`}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isCurrentPlan ? (
            "Current Plan"
          ) : (
            "Upgrade"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();

  const { data: plans, isLoading: plansLoading } = useQuery<SubscriptionPlan[]>({
    queryKey: ["/api/subscription-plans"],
  });

  const { data: subscription } = useQuery<UserSubscription>({
    queryKey: ["/api/subscription"],
  });

  const subscribeMutation = useMutation({
    mutationFn: async (planId: string) => {
      const res = await apiRequest("POST", "/api/subscription", { planId });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Subscription Updated",
        description: "Your subscription has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/subscription"] });
      refreshUser();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update subscription. Please try again.",
        variant: "destructive",
      });
    },
  });

  const currentTier = user?.subscriptionTier || "free";

  return (
    <MainLayout title="Subscription" subtitle="Manage your subscription plan">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Your Current Plan</CardTitle>
            <CardDescription>
              You are currently on the <strong className="capitalize">{currentTier}</strong> plan
              {subscription?.endDate && (
                <> (renews on {new Date(subscription.endDate).toLocaleDateString()})</>
              )}
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plansLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-48 bg-muted rounded" />
                </CardContent>
              </Card>
            ))
          ) : plans && plans.length > 0 ? (
            plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={plan.tier === currentTier}
                onSelect={() => subscribeMutation.mutate(plan.id)}
                loading={subscribeMutation.isPending}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No subscription plans available
            </div>
          )}
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="text-center text-sm text-muted-foreground">
              <p className="mb-2">
                All plans include a 14-day free trial. Cancel anytime.
              </p>
              <p>
                Need a custom plan?{" "}
                <a href="#" className="text-primary hover:underline">
                  Contact Sales
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
